import React, { createContext, useState, useContext, useEffect, useRef } from 'react';
import { loadFromStorage, saveToStorage } from '@/lib/storage';
import { useConfig } from '@/context/ConfigContext';
import { LOCAL_STORAGE_KEYS } from '@/config/constants';
import { auth, db } from '@/firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { validateTrustedDevice } from '@/lib/trustedDeviceSync';

const UserContext = createContext();

export const useUser = () => useContext(UserContext);

export const UserProvider = ({ children }) => {
  const { config, workspaceId, userRole, assignedProfileIds, adminPinHash, adminPinSalt, adminPinUpdatedAt, isConfigReady } = useConfig();

  // Perfil activo: status === 'active' o ausencia de status (legacy).
  // pending_deletion y deleting quedan excluidos de selección.
  const isActiveProfile = (profile) => (profile.status ?? 'active') === 'active';

  const [selectedUser, setSelectedUser] = useState(() => {
    const savedUser = loadFromStorage(LOCAL_STORAGE_KEYS.SELECTED_USER);
    const firstActiveProfile = config.profiles.find(isActiveProfile) ?? config.profiles[0];
    const firstActiveProfileId = firstActiveProfile?.id ?? firstActiveProfile?.name ?? 'Default';

    // Prioridad canónica: assignedProfileIds[0] (accountMemberships) sobre
    // el valor persistido en localStorage. Evita que una cuenta herede el
    // perfil seleccionado por otra cuenta en el mismo dispositivo/navegador.
    // Solo se usa si el perfil asignado ya está presente y activo en
    // config.profiles; si no, se cae al fallback previo (savedUser o
    // firstActiveProfileId), igual que antes.
    const firstAssignedProfileId = Array.isArray(assignedProfileIds) && assignedProfileIds.length > 0
      ? assignedProfileIds[0]
      : null;
    if (firstAssignedProfileId && config.profiles.some(p => (p.id ?? p.name) === firstAssignedProfileId && isActiveProfile(p))) {
      return firstAssignedProfileId;
    }

    if (savedUser && config.profiles.some(p => (p.id ?? p.name) === savedUser && isActiveProfile(p))) {
      return savedUser;
    }
    return firstActiveProfileId;
  });



  // Estado de perfiles desbloqueados: Set<profileId> guardado en memoria
  // Se limpia al logout o recarga de página
  const [unlockedProfiles, setUnlockedProfiles] = useState(new Set());
  const unlockedProfilesRef = useRef(new Set());

  // Estado de administrador desbloqueado: boolean en memoria
  // Se limpia al logout, recarga de página, o si cambian las credenciales remotas del PIN admin
  const [isAdminUnlocked, setIsAdminUnlocked] = useState(false);

  // Estado de validación de dispositivo confiable en curso
  const [trustedDeviceChecking, setTrustedDeviceChecking] = useState(false);

  // Ref para detectar cambios reales en credenciales de perfiles (pinHash/pinSalt/pinUpdatedAt)
  // y así invalidar únicamente los perfiles cuyo PIN cambió/desapareció remotamente.
  const prevProfilePinSignaturesRef = useRef({});

  // Ref para detectar cambios reales en credenciales admin (adminPinHash/adminPinSalt/adminPinUpdatedAt)
  const prevAdminPinSignatureRef = useRef(null);

  // Ref: recuerda para qué assignedProfileIds[0] ya se aplicó la selección
  // inicial forzada. Necesario porque config.profiles arranca vacío y se
  // rellena de forma asíncrona (onSnapshot de workerProfiles/perfiles); el
  // initializer de selectedUser no puede validar el perfil asignado hasta
  // que esos datos reales llegan. Sin este ref, el efecto de abajo se
  // repetiría en cada actualización de config.profiles y expulsaría al
  // usuario de cualquier perfil informativo seleccionado voluntariamente
  // después del arranque. Se resetea cuando assignedProfileIds cambia
  // (nueva sesión/cuenta), permitiendo una única reconciliación por sesión.
  const appliedAssignedProfileIdRef = useRef(null);

  useEffect(() => {
    const firstAssignedProfileId = Array.isArray(assignedProfileIds) && assignedProfileIds.length > 0
      ? assignedProfileIds[0]
      : null;

    if (!firstAssignedProfileId) {
      return;
    }

    // Ya se aplicó esta reconciliación para este assignedProfileId concreto:
    // no repetir (evita expulsar una selección voluntaria posterior).
    if (appliedAssignedProfileIdRef.current === firstAssignedProfileId) {
      return;
    }

    const assignedProfileActiveAndPresent = config.profiles.some(
      p => (p.id ?? p.name) === firstAssignedProfileId && isActiveProfile(p)
    );

    if (assignedProfileActiveAndPresent) {
      appliedAssignedProfileIdRef.current = firstAssignedProfileId;
      if (selectedUser !== firstAssignedProfileId) {
        setSelectedUser(firstAssignedProfileId);
      }
    }
  }, [assignedProfileIds, config.profiles]);

  useEffect(() => {
  if (!isConfigReady) {
    return;
  }

    const firstActiveProfile = config.profiles.find(isActiveProfile) ?? config.profiles[0];
    const firstActiveProfileId = firstActiveProfile?.id ?? firstActiveProfile?.name ?? 'Default';
    const activeProfiles = config.profiles.filter(isActiveProfile);

if (activeProfiles.length === 1 && userRole !== 'worker') {
  const onlyProfileId = activeProfiles[0].id ?? activeProfiles[0].name;

  if (selectedUser !== onlyProfileId) {
    setSelectedUser(onlyProfileId);
  }

  return;
}
    const currentIsActiveAndExists = config.profiles.some(p => (p.id ?? p.name) === selectedUser && isActiveProfile(p));

    // Permite conservar la selección de un perfil ajeno (no asignado) cuando
    // el trabajador tiene activada la consulta cruzada de clientes. Sin esta
    // excepción, este efecto revertía inmediatamente selectedUser al primer
    // perfil asignado en cuanto se seleccionaba un perfil informativo, ya
    // que config.profiles (para un worker) solo contiene sus perfiles
    // asignados.
    const canKeepInformationalSelection =
      !currentIsActiveAndExists &&
      userRole === 'worker' &&
      config.allowCrossProfileClientsConsultation === true &&
      Boolean(selectedUser);

    // Evita la carrera con el efecto de reconciliación de assignedProfileIds
    // (arriba): en el mismo ciclo de commits, ese efecto puede haber
    // programado selectedUser = firstAssignedProfileId sin que este efecto
    // vea todavía el valor actualizado (ambos leen el selectedUser de la
    // misma renderización). Si el perfil asignado ya está activo y presente
    // en config.profiles pero selectedUser aún no lo refleja, se considera
    // "reconciliación pendiente" y este efecto no debe forzar
    // firstActiveProfileId por encima de esa reconciliación en curso.
    const firstAssignedProfileId = Array.isArray(assignedProfileIds) && assignedProfileIds.length > 0
      ? assignedProfileIds[0]
      : null;
    const assignedReconciliationPending =
      Boolean(firstAssignedProfileId) &&
      selectedUser !== firstAssignedProfileId &&
      config.profiles.some(p => (p.id ?? p.name) === firstAssignedProfileId && isActiveProfile(p));

    if (!currentIsActiveAndExists && !canKeepInformationalSelection && !assignedReconciliationPending) {
      setSelectedUser(firstActiveProfileId);
    }
  }, [config.profiles, selectedUser, userRole, config.allowCrossProfileClientsConsultation, assignedProfileIds]);



  useEffect(() => {
    saveToStorage(LOCAL_STORAGE_KEYS.SELECTED_USER, selectedUser);
  }, [selectedUser]);

  // Nombre visible del perfil seleccionado (para mostrar en UI)
  const selectedUserName = React.useMemo(() => {
    const found = config.profiles.find(p => (p.id ?? p.name) === selectedUser);
    return found?.name ?? selectedUser;
  }, [config.profiles, selectedUser]);

  // Limpiar estado de desbloqueados al hacer logout (cuando auth.currentUser pasa a null)
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (!currentUser) {
  // Usuario ha hecho logout: limpiar perfiles desbloqueados y admin desbloqueado
  console.log('[USER_CONTEXT] Logout detectado, limpiando perfiles desbloqueados y admin');
  setUnlockedProfiles(new Set());
  unlockedProfilesRef.current = new Set();
  setIsAdminUnlocked(false);
  appliedAssignedProfileIdRef.current = null;
}
    });

    return () => unsubscribe();
  }, []);

  // Invalidar desbloqueo de un perfil si sus credenciales de PIN cambian remotamente
  // (pinHash/pinSalt/pinUpdatedAt), incluso si estaba desbloqueado. Cubre tanto
  // "cambio de PIN" como "PIN eliminado" (deja de requerir bloqueo, pero no debe
  // seguir considerado como "desbloqueado con credenciales antiguas": se limpia
  // igualmente del Set, ya que isProfileLocked ya no lo bloqueará al no tener pinHash).
  useEffect(() => {
    const prevSignatures = prevProfilePinSignaturesRef.current;
    const nextSignatures = {};
    let changedProfileIds = [];

    (config.profiles || []).forEach((profile) => {
      const profileId = profile.id ?? profile.name;
      const signature = `${profile.pinHash || ''}|${profile.pinSalt || ''}|${profile.pinUpdatedAt?.seconds ?? profile.pinUpdatedAt ?? ''}`;
      nextSignatures[profileId] = signature;

      if (prevSignatures[profileId] !== undefined && prevSignatures[profileId] !== signature) {
        changedProfileIds.push(profileId);
      }
    });

    prevProfilePinSignaturesRef.current = nextSignatures;

    if (changedProfileIds.length > 0) {
      setUnlockedProfiles((prev) => {
        const next = new Set(prev);
        let removedAny = false;
        changedProfileIds.forEach((profileId) => {
          if (next.has(profileId)) {
            next.delete(profileId);
            removedAny = true;
          }
        });
        if (removedAny) {
          unlockedProfilesRef.current = next;
          console.log('[USER_CONTEXT] PIN de perfil cambiado remotamente, invalidando desbloqueo:', changedProfileIds);
          return next;
        }
        return prev;
      });
    }
  }, [config.profiles]);

  // Invalidar desbloqueo admin si las credenciales del PIN admin cambian remotamente
  // (adminPinHash/adminPinSalt/adminPinUpdatedAt), incluso si estaba desbloqueado.
  useEffect(() => {
    const signature = `${adminPinHash || ''}|${adminPinSalt || ''}|${adminPinUpdatedAt?.seconds ?? adminPinUpdatedAt ?? ''}`;
    const prevSignature = prevAdminPinSignatureRef.current;

    if (prevSignature !== null && prevSignature !== signature) {
      console.log('[USER_CONTEXT] PIN admin cambiado remotamente, invalidando desbloqueo admin');
      setIsAdminUnlocked(false);
    }

    prevAdminPinSignatureRef.current = signature;
  }, [adminPinHash, adminPinSalt, adminPinUpdatedAt]);

   // Validar dispositivo confiable cuando se selecciona un perfil con PIN
   useEffect(() => {
     const validateDevice = async () => {
       const profile = config.profiles?.find(p => (p.id ?? p.name) === selectedUser);
       
       // Solo validar si: perfil existe, tiene PIN, workspaceId, y db está disponible
       if (!profile?.pinHash || !workspaceId || !db) {
         setTrustedDeviceChecking(false);
         return;
       }

       // [TRUSTED_DEVICE_DIAG] Log valores antes de validar
       console.log('[TRUSTED_DEVICE_DIAG] Pre-validation:', {
         workspaceId,
         selectedUser,
         profileId: profile.id ?? profile.name,
         profileName: profile.name,
         hasPinHash: !!profile.pinHash,
         pinUpdatedAt: profile.pinUpdatedAt,
       });

       setTrustedDeviceChecking(true);
       try {
         const isValid = await validateTrustedDevice(
           workspaceId,
           db,
           selectedUser,
           profile.pinUpdatedAt
         );

        if (isValid) {
          unlockProfile(selectedUser);
        }
      } catch (error) {
        console.error('[USER_CONTEXT] Error validating trusted device:', error);
      } finally {
        setTrustedDeviceChecking(false);
      }
    };

    validateDevice();
  }, [selectedUser, config.profiles, workspaceId, db]);

   // Intenta desbloquear un perfil arbitrario (no necesariamente selectedUser)
   // usando el dispositivo confiable registrado para ese profileId.
   // No cambia selectedUser. Devuelve true si quedó desbloqueado, false si no.
   const tryUnlockWithTrustedDevice = async (profileId) => {
     const profile = config.profiles?.find(p => (p.id ?? p.name) === profileId);

     if (!profile?.pinHash) {
       return true;
     }

     if (!workspaceId || !profileId || !db) {
       return false;
     }

     setTrustedDeviceChecking(true);
     try {
       const isValid = await validateTrustedDevice(
         workspaceId,
         db,
         profileId,
         profile.pinUpdatedAt
       );

       if (isValid) {
         unlockProfile(profileId);
         return true;
       }

       return false;
     } catch (error) {
       console.error('[USER_CONTEXT] Error validating trusted device (tryUnlockWithTrustedDevice):', error);
       return false;
     } finally {
       setTrustedDeviceChecking(false);
     }
   };

   // Funciones para gestionar lock/unlock de perfiles


   const lockProfile = (profileId) => {
     const newUnlocked = new Set(unlockedProfilesRef.current);
     newUnlocked.delete(profileId);
     unlockedProfilesRef.current = newUnlocked;
     setUnlockedProfiles(newUnlocked);
   };

   const unlockProfile = (profileId) => {
     const newUnlocked = new Set(unlockedProfilesRef.current);
     newUnlocked.add(profileId);
     unlockedProfilesRef.current = newUnlocked;
     setUnlockedProfiles(newUnlocked);
   };

   // Cambio seguro de perfil: bloquea el anterior (si distinto),
   // mantiene desbloqueado solo el nuevo si es necesario, luego cambia selectedUser
   const switchProfile = (newProfileId, { alreadyUnlocked = false } = {}) => {
     const prevProfileId = selectedUser;
     
     // Si es el mismo perfil, no hacer nada salvo desbloquearlo si corresponde
     if (prevProfileId === newProfileId) {
       if (alreadyUnlocked) {
         unlockProfile(newProfileId);
       }
       return;
     }

     // Bloquear el perfil anterior si es distinto
     if (prevProfileId && prevProfileId !== newProfileId) {
       lockProfile(prevProfileId);
     }

     // Desbloquear el nuevo si ya fue acertado
     if (alreadyUnlocked) {
       unlockProfile(newProfileId);
     }

     // Cambiar perfil activo
     setSelectedUser(newProfileId);
   };

  const isProfileLocked = (profileId) => {
    const profile = config.profiles.find(p => (p.id ?? p.name) === profileId);
    // Si tiene PIN y no está en el set de desbloqueados, está bloqueado
    if (profile?.pinHash && !unlockedProfiles.has(profileId)) {
      return true;
    }
    return false;
  };

  // Funciones para gestionar lock/unlock del administrador (rueda amarilla)
  const unlockAdmin = () => {
    setIsAdminUnlocked(true);
  };

  const lockAdmin = () => {
    setIsAdminUnlocked(false);
  };

  return (
    <UserContext.Provider value={{
      selectedUser,
      setSelectedUser,
      selectedUserName,
      unlockedProfiles,
      lockProfile,
      unlockProfile,
      switchProfile,
      isProfileLocked,
      tryUnlockWithTrustedDevice,
      isAdminUnlocked,
      unlockAdmin,
      lockAdmin,
      trustedDeviceChecking,
    }}>
      {children}
    </UserContext.Provider>
  );
};
